import { useState } from 'react';
import { Link2, Unlink2, Check, AlertCircle, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/I18nContext';
import { useToast } from '@/context/ToastContext';
import { rpc } from '@/api/client';
import { getGoogleClientId, getAppleClientId } from '@/lib/config';
import { redirectToGoogleAuth, redirectToAppleAuth } from '@shared/auth/oauth';
import { ConnectedAppsSection } from './ConnectedAppsSection';

export function AccountSettingsSection() {
  const { user, signOut, refreshUser } = useAuth();
  const { t } = useI18n();
  const { showToast } = useToast();

  const [unlinkingProvider, setUnlinkingProvider] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const expectedDeletePhrase = t('settings.deleteAccountConfirmPhrase');
  const isDeletePhraseMatched =
    deleteConfirmInput.trim().toLowerCase() === expectedDeletePhrase.trim().toLowerCase();

  const googleLinked = !!user?.googleLinked;
  const appleLinked = !!user?.appleLinked;
  const linkedCount = (googleLinked ? 1 : 0) + (appleLinked ? 1 : 0);

  const handleLinkGoogle = () => {
    const clientId = getGoogleClientId();
    if (!clientId) {
      showToast('Google Client ID not configured', 'error');
      return;
    }
    // Google doesn't easily encode custom state with implicit flow in our setup,
    // so we redirect to Google auth with return path
    redirectToGoogleAuth(clientId, window.location.hash || '#/settings');
  };

  const handleLinkApple = () => {
    const clientId = getAppleClientId();
    if (!clientId) {
      showToast('Apple Client ID not configured', 'error');
      return;
    }
    if (!user) return;
    redirectToAppleAuth(clientId, window.location.hash || '#/settings', user.id);
  };

  const handleUnlink = async (provider: 'google' | 'apple') => {
    if (linkedCount <= 1) {
      showToast(t('settings.cannotUnlinkOnlyProvider'), 'error');
      return;
    }

    setUnlinkingProvider(provider);
    try {
      await rpc('UnlinkOAuthProvider', { provider });
      await refreshUser();
      showToast(`${provider === 'google' ? 'Google' : 'Apple'} unlinked successfully`, 'success');
    } catch (err: any) {
      showToast(err?.message || `Failed to unlink ${provider}`, 'error');
    } finally {
      setUnlinkingProvider(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!isDeletePhraseMatched || isDeletingAccount) return;
    setIsDeletingAccount(true);
    try {
      await rpc('DeleteAccount', {});
      await signOut();
      window.location.hash = '#/login';
    } catch (err: any) {
      showToast(err?.message || 'Failed to delete account', 'error');
      setIsDeletingAccount(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── User Overview ── */}
      <div className="card p-5">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent text-lg font-semibold shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-text-primary truncate">{user?.name || '—'}</p>
          </div>
        </div>
      </div>

      {/* ── Linked Accounts ── */}
      <div className="card p-5 space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <Link2 size={16} className="text-accent" />
            {t('settings.linkedAccounts')}
          </h4>
          {linkedCount <= 1 && (
            <p className="text-xs text-text-muted mt-1 flex items-center gap-1">
              <AlertCircle size={12} className="text-amber-400" />
              {t('settings.cannotUnlinkOnlyProvider')}
            </p>
          )}
        </div>

        <div className="divide-y divide-border-subtle">
          {/* Google provider */}
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-bg-surface border border-border-default flex items-center justify-center text-xs font-bold text-text-primary">
                G
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Google</p>
                <p className="text-xs text-text-muted">
                  {googleLinked ? (
                    <span className="text-emerald-400 flex items-center gap-1">
                      <Check size={12} /> {t('settings.linked')}
                    </span>
                  ) : (
                    t('settings.notLinked')
                  )}
                </p>
              </div>
            </div>

            <div>
              {googleLinked ? (
                <button
                  type="button"
                  disabled={linkedCount <= 1 || unlinkingProvider === 'google'}
                  onClick={() => handleUnlink('google')}
                  className="px-3 py-1 rounded-md text-xs font-medium text-text-secondary border border-border-default hover:border-red-500/50 hover:text-red-500 disabled:opacity-40 disabled:hover:border-border-default disabled:hover:text-text-secondary disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <Unlink2 size={12} />
                  <span>{t('settings.unlink')}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleLinkGoogle}
                  className="px-3 py-1 rounded-md text-xs font-medium bg-bg-surface hover:bg-bg-hover text-text-primary border border-border-default transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <Link2 size={12} />
                  <span>{t('settings.linkGoogle')}</span>
                </button>
              )}
            </div>
          </div>

          {/* Apple provider */}
          <div className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-bg-surface border border-border-default flex items-center justify-center text-xs font-bold text-text-primary">
                
              </div>
              <div>
                <p className="text-sm font-medium text-text-primary">Apple</p>
                <p className="text-xs text-text-muted">
                  {appleLinked ? (
                    <span className="text-emerald-400 flex items-center gap-1">
                      <Check size={12} /> {t('settings.linked')}
                    </span>
                  ) : (
                    t('settings.notLinked')
                  )}
                </p>
              </div>
            </div>

            <div>
              {appleLinked ? (
                <button
                  type="button"
                  disabled={linkedCount <= 1 || unlinkingProvider === 'apple'}
                  onClick={() => handleUnlink('apple')}
                  className="px-3 py-1 rounded-md text-xs font-medium text-text-secondary border border-border-default hover:border-red-500/50 hover:text-red-500 disabled:opacity-40 disabled:hover:border-border-default disabled:hover:text-text-secondary disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <Unlink2 size={12} />
                  <span>{t('settings.unlink')}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleLinkApple}
                  className="px-3 py-1 rounded-md text-xs font-medium bg-bg-surface hover:bg-bg-hover text-text-primary border border-border-default transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <Link2 size={12} />
                  <span>{t('settings.linkApple')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Connected AI Applications (OAuth) ── */}
      <ConnectedAppsSection />

      {/* ── Account Deletion (Danger Zone) ── */}
      <div className="card p-5 border border-red-500/20 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h4 className="text-sm font-semibold text-red-400 flex items-center gap-2">
              <Trash2 size={16} />
              {t('settings.deleteAccount')}
            </h4>
            <p className="text-xs text-text-muted mt-1 max-w-xl">
              {t('settings.deleteAccountDesc')}
            </p>
          </div>
          <button
            type="button"
            data-testid="delete-account-button"
            onClick={() => {
              setDeleteConfirmInput('');
              setShowDeleteConfirm(true);
            }}
            className="self-start sm:self-center shrink-0 px-3.5 py-1.5 rounded-md text-xs font-medium text-red-400 border border-red-500/40 hover:bg-red-500/10 hover:border-red-500 transition-colors cursor-pointer flex items-center gap-1.5"
          >
            <Trash2 size={13} />
            <span>{t('settings.deleteAccount')}</span>
          </button>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-fade-in">
          <div className="card w-full max-w-md p-5 space-y-4 shadow-xl border border-red-500/30 bg-bg-raised">
            <h3 className="text-base font-semibold flex items-center gap-2 text-red-400">
              <AlertTriangle size={18} />
              {t('settings.deleteAccountConfirmTitle')}
            </h3>
            <p className="text-xs text-text-secondary leading-relaxed">
              {t('settings.deleteAccountConfirmDesc')}
            </p>
            <div className="space-y-2 pt-1">
              <label
                htmlFor="delete-account-confirm-input"
                className="block text-xs font-medium text-text-secondary"
              >
                {t('settings.deleteAccountConfirmPrompt', { phrase: expectedDeletePhrase })}
              </label>
              <input
                id="delete-account-confirm-input"
                type="text"
                data-testid="delete-account-confirm-input"
                value={deleteConfirmInput}
                onChange={(e) => setDeleteConfirmInput(e.target.value)}
                placeholder={expectedDeletePhrase}
                autoComplete="off"
                autoFocus
                disabled={isDeletingAccount}
                className="w-full px-3 py-2 rounded-md text-xs font-mono bg-bg-surface border border-border-default focus:border-red-500 focus:ring-1 focus:ring-red-500/40 text-text-primary placeholder:text-text-muted/50 outline-none transition-colors"
              />
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                data-testid="cancel-delete-account-button"
                disabled={isDeletingAccount}
                onClick={() => {
                  setDeleteConfirmInput('');
                  setShowDeleteConfirm(false);
                }}
                className="px-3.5 py-1.5 rounded-md text-xs font-medium text-text-secondary hover:text-text-primary border border-border-default hover:bg-bg-hover transition-colors cursor-pointer"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                data-testid="confirm-delete-account-button"
                disabled={!isDeletePhraseMatched || isDeletingAccount}
                onClick={handleDeleteAccount}
                className="px-4 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center gap-1.5"
              >
                <Trash2 size={13} />
                <span>{isDeletingAccount ? t('common.loading') : t('settings.deleteAccountConfirmButton')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
